import React, { useEffect, useMemo, useState } from 'react'
import api from '../../utils/api'
import '../../styles/CertificateManagement.css'
import Pagination from '../../components/common/Pagination'

export default function CertificateManagement() {
  const [certificates, setCertificates] = useState([])
  const [loading, setLoading] = useState(true)

  const [selected, setSelected] = useState([])

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  useEffect(() => {
    loadCertificates()
  }, [])

  const loadCertificates = async () => {
    try {
      setLoading(true)

      const res = await api.get('/certificates')

      setCertificates(res.data.data || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const filteredCertificates = useMemo(() => {
    return certificates.filter((cert) => {
      const matchesSearch =
        cert.certificate_id?.toLowerCase().includes(search.toLowerCase()) ||
        cert.student?.user?.name
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        cert.student?.roll_number?.toLowerCase().includes(search.toLowerCase())

      const matchesStatus = !statusFilter || cert.status === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [certificates, search, statusFilter])

  const paginatedCertificates = useMemo(() => {
    return filteredCertificates.slice((page - 1) * limit, page * limit)
  }, [filteredCertificates, page, limit])

  const stats = useMemo(() => {
    return {
      total: certificates.length,

      generated: certificates.filter((x) => x.status === 'generated').length,

      revoked: certificates.filter((x) => x.status === 'revoked').length,

      pdfGenerated: certificates.filter((x) => x.pdf_path).length,

      pdfPending: certificates.filter((x) => !x.pdf_path).length,
    }
  }, [certificates])

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(paginatedCertificates.map((c) => c.id))
    } else {
      setSelected([])
    }
  }

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }
  const handleView = (certificateId) => {
    window.open(`/certificates/${certificateId}`, '_blank')
  }

  const handleDownload = async (certificateId) => {
    try {
      const res = await api.post(`/certificates/${certificateId}/generate-pdf`)

      const fileUrl = `${process.env.REACT_APP_API_URL}/${res.data.pdf_path}`

      const response = await fetch(fileUrl)

      const blob = await response.blob()

      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')

      link.href = blobUrl

      link.download = `${certificateId}.pdf`

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)

      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error(err)

      alert('Failed to download PDF')
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'generated':
        return 'status-generated'

      case 'revoked':
        return 'status-revoked'

      default:
        return 'status-pending'
    }
  }

  const handleBulkGeneratePDF = async () => {
    if (!selected.length) {
      return alert('Select certificates first')
    }

    try {
      const studentIds = certificates
        .filter((c) => selected.includes(c.id))
        .map((c) => c.student.id)

      await api.post('/certificates/bulk-generate-pdf', {
        studentIds,
      })

      alert('PDFs generated successfully')

      loadCertificates()
    } catch (err) {
      console.error(err)

      alert('Failed to generate PDFs')
    }
  }

  const handleBulkDownload = async () => {
    if (!selected.length) {
      return alert('Select certificates first')
    }

    try {
      const studentIds = certificates
        .filter((c) => selected.includes(c.id))
        .map((c) => c.student.id)

      const res = await api.post('/certificates/bulk-download', {
        studentIds,
      })

      const zipUrl = `${process.env.REACT_APP_API_URL}/${res.data.data.zip_path}`

      const link = document.createElement('a')

      link.href = zipUrl

      link.download = 'certificates.zip'

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    } catch (err) {
      console.error(err)

      alert('Failed to download ZIP')
    }
  }

  const handleDeleteSelected = async () => {
    if (!selected.length) {
      return alert('Select certificates first')
    }

    if (!window.confirm(`Delete ${selected.length} certificates?`)) {
      return
    }

    try {
      await api.post('/certificates/bulk-delete', {
        ids: selected,
      })

      alert('Certificates deleted')

      setSelected([])

      loadCertificates()
    } catch (err) {
      console.error(err)

      alert('Delete failed')
    }
  }

  const handleDeleteSingle = async (certificateId) => {
    if (!window.confirm('Delete certificate?')) {
      return
    }

    try {
      await api.delete(`/certificates/${certificateId}`)

      alert('Certificate deleted')

      loadCertificates()
    } catch (err) {
      console.error(err)

      alert('Delete failed')
    }
  }

  const handleRevoke = async (certificateId) => {
    try {
      await api.patch(`/certificates/${certificateId}/revoke`)

      alert('Certificate revoked')

      loadCertificates()
    } catch (err) {
      console.error(err)

      alert('Failed to revoke')
    }
  }

  const handleRestore = async (certificateId) => {
    try {
      await api.patch(`/certificates/${certificateId}`, {
        status: 'generated',
      })

      alert('Certificate restored')

      loadCertificates()
    } catch (err) {
      console.error(err)

      alert('Failed to restore')
    }
  }

  if (loading) {
    return <div className="page-container">Loading...</div>
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex',  justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'nowrap' }}>
          <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Certificate Management</h2>
          <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={loadCertificates}>
            Refresh
          </button>
        </div>
        <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)' }}>Manage certificates, PDFs and downloads</p>
      </div>

      {/* Stats */}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div className="stat-card" style={{ padding: '12px' }}>
          <div className="stat-value" style={{ fontSize: '1.3rem' }}>{stats.total}</div>
          <div className="stat-label" style={{ fontSize: '0.7rem' }}>Total Certificates</div>
        </div>

        <div className="stat-card" style={{ padding: '12px' }}>
          <div className="stat-value" style={{ fontSize: '1.3rem' }}>{stats.generated}</div>
          <div className="stat-label" style={{ fontSize: '0.7rem' }}>Generated</div>
        </div>

        <div className="stat-card" style={{ padding: '12px' }}>
          <div className="stat-value" style={{ fontSize: '1.3rem' }}>{stats.revoked}</div>
          <div className="stat-label" style={{ fontSize: '0.7rem' }}>Revoked</div>
        </div>

        <div className="stat-card" style={{ padding: '12px' }}>
          <div className="stat-value" style={{ fontSize: '1.3rem' }}>{stats.pdfGenerated}</div>
          <div className="stat-label" style={{ fontSize: '0.7rem' }}>PDF Generated</div>
        </div>
      </div>

      {/* Filters */}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <input
            className="input"
            style={{ width: '100%' }}
            type="text"
            placeholder="Search certificate, student, roll no..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />

          <select
            className="input"
            style={{ width: '100%' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <option value="">All Status</option>
            <option value="generated">Generated</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      </div>

      {/* Bulk Action Toolbar */}

      {/* Bulk Action Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          Selected:
          <strong> {selected.length}</strong>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleBulkGeneratePDF}>
            Generate PDFs
          </button>

          <button className="btn btn-success" onClick={handleBulkDownload}>
            Download ZIP
          </button>

          <button className="btn btn-danger" onClick={handleDeleteSelected}>
            Delete Selected
          </button>
        </div>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="certificate-table" style={{ minWidth: 800 }}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selected.length === paginatedCertificates.length &&
                    paginatedCertificates.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>

              <th>Certificate ID</th>

              <th>Student</th>

              <th>Roll No</th>

              <th>Course</th>

              <th>Batch</th>

              <th>Status</th>

              <th>PDF</th>

              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredCertificates.length === 0 ? (
              <tr>
                <td
                  colSpan="9"
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                  }}
                >
                  No certificates found
                </td>
              </tr>
            ) : (
              paginatedCertificates.map((cert) => (
                <tr key={cert.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(cert.id)}
                      onChange={() => handleSelect(cert.id)}
                    />
                  </td>
                  <td>{cert.certificate_id}</td>
                  <td>{cert.student?.user?.name}</td>
                  <td>{cert.roll_number}</td>
                  <td>{cert.course?.name}</td>
                  <td>{cert.batch?.batch_code}</td>
                  <td>
                    <span
                      className={`status-badge ${getStatusClass(cert.status)}`}
                    >
                      {cert.status}
                    </span>
                  </td>
                  <td>{cert.pdf_path ? 'Generated' : 'Not Generated'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-view"
                        onClick={() => handleView(cert.certificate_id)}
                      >
                        View
                      </button>

                      <button
                        className="btn-download"
                        onClick={() => handleDownload(cert.certificate_id)}
                      >
                        Download
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteSingle(cert.certificate_id)}
                      >
                        Delete
                      </button>
                      {cert.status !== 'revoked' ? (
                        <button
                          className="btn-revoke"
                          onClick={() => handleRevoke(cert.certificate_id)}
                        >
                          Revoke
                        </button>
                      ) : (
                        <button
                          className="btn-restore"
                          onClick={() => handleRestore(cert.certificate_id)}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredCertificates.length > 0 && (
        <Pagination 
          page={page} 
          setPage={setPage} 
          limit={limit} 
          setLimit={setLimit} 
          total={filteredCertificates.length} 
        />
      )}
    </div>
  )
}
